import { NetworkStatus } from "@apollo/client";
import { useCallback, useMemo } from "react";
import { useWindowDimensions } from "react-native";

import OrganizationsView from "./OrganizationsView";
import ListEmpty from "@/features/invitations/components/molecules/ListEmpty";
import OrganizationItem from "@/features/invitations/components/molecules/OrganizationItem";

import SkeletonList from "@/components/organisms/SkeletonList";
import { ListRenderItem } from "@/components/atoms/FlashList";
import { Spinner } from "@/components/atoms/Spinner";
import { useCurrentUser } from "@/features/auth";
import {
  InvitationsOrganizationFragment,
  useInvitationsListMyOrganizationsQuery,
  useInvitationsListOrganizationsQuery,
} from "@/generated/graphql";
import useSetPageTitle from "@/hooks/useSetPageTitle";
import { useTheme } from "@/providers/ThemeProvider";
import { ifWeb } from "@/utils/platform";

/**
 * NativeWind `md:` breakpoint (768px). The content panel fixes its width at md+
 * and otherwise fills the viewport — a screen-width decision derived from the
 * measured window width rather than the vendored Gluestack breakpoint hook.
 */
const MD_BREAKPOINT = 768;

/**
 * Container component for the organizations listing screen.
 * Displays all or user-specific organizations based on admin status.
 */
const OrganizationsContainer = () => {
  const currentUser = useCurrentUser();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const {
    data: globalData,
    fetchMore: globalFetchMore,
    loading: globalLoading,
    refetch: globalRefetch,
    networkStatus: globalNetworkStatus,
  } = useInvitationsListOrganizationsQuery({
    skip: !currentUser?.isAdmin,
  });
  const {
    data: myData,
    fetchMore: myFetchMore,
    loading: myLoading,
    refetch: myRefetch,
    networkStatus: myNetworkStatus,
  } = useInvitationsListMyOrganizationsQuery();

  const organizations = useMemo(
    () =>
      currentUser?.isAdmin
        ? (globalData?.listOrganizations?.edges.map(edge => edge.node) ?? [])
        : (myData?.listMyOrganizations?.edges.map(edge => edge.node) ?? []),
    [
      currentUser?.isAdmin,
      globalData?.listOrganizations?.edges,
      myData?.listMyOrganizations?.edges,
    ]
  );

  const endCursor = useMemo(() => {
    if (currentUser?.isAdmin) {
      if (globalData?.listOrganizations?.pageInfo.hasNextPage) {
        return globalData?.listOrganizations?.pageInfo.endCursor;
      }
      return null;
    } else {
      if (myData?.listMyOrganizations?.pageInfo.hasNextPage) {
        return myData?.listMyOrganizations?.pageInfo.endCursor;
      }
      return null;
    }
  }, [
    currentUser?.isAdmin,
    globalData?.listOrganizations?.pageInfo.endCursor,
    globalData?.listOrganizations?.pageInfo.hasNextPage,
    myData?.listMyOrganizations?.pageInfo.endCursor,
    myData?.listMyOrganizations?.pageInfo.hasNextPage,
  ]);

  const isRefreshing = useMemo(
    () =>
      currentUser?.isAdmin
        ? globalNetworkStatus === NetworkStatus.refetch
        : myNetworkStatus === NetworkStatus.refetch,
    [currentUser?.isAdmin, globalNetworkStatus, myNetworkStatus]
  );

  const fetchMore = useMemo(
    () => (currentUser?.isAdmin ? globalFetchMore : myFetchMore),
    [currentUser?.isAdmin, globalFetchMore, myFetchMore]
  );

  const refetch = useMemo(
    () => (currentUser?.isAdmin ? globalRefetch : myRefetch),
    [currentUser?.isAdmin, globalRefetch, myRefetch]
  );

  const loading = globalLoading || myLoading;

  const handleLoadMore = useCallback(() => {
    if (endCursor && !loading) {
      fetchMore({
        variables: { after: endCursor },
      });
    }
  }, [endCursor, fetchMore, loading]);

  const handleRefresh = useCallback(() => refetch().catch(() => {}), [refetch]);

  const renderItem: ListRenderItem<InvitationsOrganizationFragment> =
    useCallback(({ item }) => <OrganizationItem item={item} />, []);

  const renderListFooterComponent = useCallback(
    () =>
      loading && !isRefreshing && organizations.length ? (
        <Spinner size="large" color={isDark ? "white" : "black"} />
      ) : null,
    [loading, isRefreshing, organizations.length, isDark]
  );

  const renderListEmptyComponent = useCallback(
    () =>
      loading ? (
        <SkeletonList type="manage-orgs" />
      ) : (
        <ListEmpty
          title="No Organizations"
          description="Click the plus button above to add organizations to this list"
          isDark={isDark}
        />
      ),
    [loading, isDark]
  );

  const { width } = useWindowDimensions();
  const widthContent = width >= MD_BREAKPOINT ? ifWeb(640, 750) : width;

  useSetPageTitle("Organizations");

  return (
    <OrganizationsView
      organizations={organizations}
      renderItem={renderItem}
      widthContent={widthContent}
      refreshing={isRefreshing}
      renderListFooterComponent={renderListFooterComponent}
      renderListEmptyComponent={renderListEmptyComponent}
      onLoadMore={handleLoadMore}
      onRefresh={handleRefresh}
      isDark={isDark}
    />
  );
};

export default OrganizationsContainer;
